import { Button } from '@heidi/design-system/button';
import { Link } from '@heidi/design-system/link';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronRight, ImageOff } from 'lucide-react';
import type { CSSProperties } from 'react';

/**
 * One horizontal diagnosis row: optional rank chip, square thumbnail, name,
 * optional one-line description, optional badge, optional ordinal bar, and a
 * trailing chevron. Shared by ``VisualDxRankedDifferential`` (LLM-ranked
 * list) and ``VisualDxDrugReaction`` (VisualDx's own drug DDx) so the two
 * lists read as the same surface in the chat.
 *
 * Interaction is one of:
 * - ``href``: the whole row is an external link (design-system ``Link``).
 * - ``onClick``: the whole row is a ``Button``; when neither is given the
 *   row still renders as a disabled ``Button`` so the read-only variant keeps
 *   the same box model and focus order as the interactive one.
 *
 * SaMD note: ``bar.pct`` only sets the fill width. No numeric value is
 * rendered or exposed to ATs, so the bar reads as relative ranking rather
 * than a calibrated clinical probability. Callers own the ``ariaLabel``.
 *
 * Presentational only; no strings of its own.
 */

export interface VisualDxDiagnosisRowBar {
  /** 0..100; clamped here. */
  pct: number;
  /** Lets each caller keep its own test hook on the fill element. */
  testId: string;
}

export interface VisualDxDiagnosisRowBadge {
  label: string;
  className: string;
}

export interface VisualDxDiagnosisRowProps {
  name: string;
  imageUrl: string | null;
  ariaLabel: string;
  rank?: number;
  description?: string | null;
  badge?: VisualDxDiagnosisRowBadge;
  bar?: VisualDxDiagnosisRowBar;
  /** Small caption under the bar, e.g. a SaMD-tone disclaimer. */
  footnote?: string;
  href?: string | null;
  onClick?: () => void;
  /** Defaults to ``Boolean(href)``. */
  showChevron?: boolean;
}

export const diagnosisRowClass =
  'flex w-full items-stretch gap-3 rounded-[10px] border border-border bg-surface p-3 text-left transition-colors';

const interactiveRowClass = 'hover:border-border-hover hover:bg-surface-hover';

const rankChipClass =
  'mt-1 flex size-6 shrink-0 items-center justify-center self-start rounded-full bg-background-primary text-xs font-bold text-text-primary-on-dark';

const thumbClass = 'size-14 shrink-0 rounded-lg bg-background-tertiary object-cover';

const thumbPlaceholderClass = cn(
  thumbClass,
  'flex items-center justify-center text-text-tertiary'
);

const bodyColumnClass = 'flex min-w-0 flex-1 flex-col justify-center gap-1.5';

const nameRowClass = 'flex items-center justify-between gap-2';

const badgeBaseClass =
  'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap';

const barTrackClass = 'h-1 flex-1 overflow-hidden rounded-full bg-background-tertiary';

const barFillClass = 'h-full bg-background-primary';

// Design-system ``Button`` sets ``white-space: nowrap`` and it inherits, so
// the clamp has to reset it on the element that owns the clamp; inline so no
// downstream class can undo it.
const clampTwoLinesStyle: CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

export const VisualDxDiagnosisRow = ({
  name,
  imageUrl,
  ariaLabel,
  rank,
  description,
  badge,
  bar,
  footnote,
  href,
  onClick,
  showChevron,
}: VisualDxDiagnosisRowProps): JSX.Element => {
  const chevron = showChevron ?? Boolean(href);
  const pct = bar ? Math.max(0, Math.min(100, bar.pct)) : null;

  const content = (
    <>
      {rank != null ? (
        <span className={rankChipClass} aria-hidden>
          {rank}
        </span>
      ) : null}
      {imageUrl ? (
        // VisualDx serves per-token CDN URLs or inline base64; next/image
        // domain whitelisting doesn't apply. Lazy + no-referrer matches the
        // other Evidence image surfaces.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={imageUrl}
          alt=""
          className={thumbClass}
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className={thumbPlaceholderClass} aria-hidden>
          <ImageOff className="size-5" />
        </div>
      )}
      <div className={bodyColumnClass}>
        <div className={nameRowClass}>
          <TypographyP2Bold className="min-w-0 truncate text-text-primary" title={name}>
            {name}
          </TypographyP2Bold>
          {badge ? (
            <span className={cn(badgeBaseClass, badge.className)}>{badge.label}</span>
          ) : null}
        </div>
        {description ? (
          <TypographyCaption
            className="min-w-0 text-text-secondary"
            style={clampTwoLinesStyle}
            title={description}
          >
            {description}
          </TypographyCaption>
        ) : null}
        {pct != null ? (
          <div className="mt-0.5 flex items-center gap-2" aria-hidden>
            <div className={barTrackClass}>
              <div
                className={barFillClass}
                style={{ width: `${pct}%` }}
                data-testid={bar?.testId}
              />
            </div>
          </div>
        ) : null}
        {footnote ? (
          <TypographyCaption className="text-text-tertiary">{footnote}</TypographyCaption>
        ) : null}
      </div>
      {chevron ? (
        <ChevronRight
          className="size-4 shrink-0 self-center text-text-tertiary rtl:-scale-x-100"
          aria-hidden
        />
      ) : null}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        variant="inline"
        className={cn(
          diagnosisRowClass,
          interactiveRowClass,
          'no-underline hover:no-underline'
        )}
        aria-label={ariaLabel}
      >
        {content}
      </Link>
    );
  }

  const clickable = Boolean(onClick);
  return (
    <Button
      size="lg"
      variant="ghost"
      disableAutomaticTypographyClasses
      disableAutomaticSvgSizing
      disabled={!clickable}
      onClick={onClick}
      className={cn(
        diagnosisRowClass,
        'h-auto justify-start',
        clickable && interactiveRowClass
      )}
      // Belt-and-braces: Button can apply its own width utilities after
      // cn(), which previously undid w-full and let long descriptions grow
      // the card past the chat bubble.
      style={{ width: '100%', maxWidth: '100%' }}
      aria-label={ariaLabel}
    >
      {content}
    </Button>
  );
};
